import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Modal,
  Button,
  ListItem,
  List,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  MoreVert,
  InfoOutlined,
  Close,
  InboxOutlined,
} from "@mui/icons-material";
import axios from "axios";

import {
  RoleChip,
  CustomTooltip,
  formatDate,
  DomainCell,
} from "./commonComponents";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import EmailConfirmationModal from "./EmailConfirmationModal";

const EmailAction = ({
  theme,
  fetchThemeData,
  page,
  limit,
  settings,
  templates,
}) => {
  const [open, setOpen] = useState(false);
  const [loadingType, setLoadingType] = useState(null);
  const [activeTab, setActiveTab] = useState("history");
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoverField, setHoverField] = useState(null);
  const [toast, setToast] = useState({ success: false, error: false, msg: "" });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const MIN_LOADING_TIME = 500;


  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "55%",
    bgcolor: "#f0f0f0ff",
    borderRadius: 5,
    height: "100%",
    maxHeight: "60%",
    boxShadow: 24,
    px: 2,
    py: 1.2,
  };

  const handleSendMail = useCallback(async (theme, emailType) => {
    try {
      setLoadingType(emailType);

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/send-email`,
        {
          theme,
          emailType,
        }
      );

      setToast({
        success: data.status,
        error: !data.status,
        msg: data?.message ?? "Email sent successfully",
      });

      if (data.status) fetchThemeData(page, limit);
    } catch (error) {
      setToast({
        success: false,
        error: true,
        msg: error?.response?.data?.message ?? "Error sending email",
      });
    } finally {
      setLoadingType(null);
    }
  }, []);
  // const mergedUniqueEmails = [
  //   ...(settings?.emailOptions || []),
  //   ...(emailOptions || []).filter((item) =>
  //     (settings?.emailOptions || []).some((elm) => elm.id !== item.id)
  //   ),
  // ];
  const updateSubscription = async (domain, themeSchemaName, unSubscribed) => {
    const startTime = Date.now();

    try {
      setSubscriptionLoading(true);

      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-subscription`,
        { domain, themeSchemaName, unSubscribed },
        { withCredentials: true }
      );

      // Baaki minimum time complete karo agar API bahut fast thi
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOADING_TIME) {
        await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_TIME - elapsed));
      }

      setToast({
        success: true,
        error: false,
        msg: data?.message ?? "Subscription updated successfully",
      });

      fetchThemeData(page, limit);
    } catch (error) {
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOADING_TIME) {
        await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_TIME - elapsed));
      }

      setToast({
        success: false,
        error: true,
        msg: error?.response?.data?.message ?? "Update failed",
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const emailTab = useMemo(() => {
    return (
      <TableContainer component={Box}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Template</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {Object.entries(templates)?.map(
              ([key, { name, id, supportedThemerole }]) => {
                const sent = theme?.emailSent?.[id] || {};
                const isDisabled = loadingType === id;
                // ||!supportedThemerole?.includes(theme.themeRole);

                return (
                  <TableRow
                    key={id}
                    sx={{
                      opacity: isDisabled ? 0.6 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                      cursor: isDisabled ? "not-allowed" : "default",
                    }}
                  >
                    <TableCell>{name}</TableCell>

                    <TableCell align="right" style={{ width: 150 }}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {loadingType === id ? (
                          <CircularProgress size={18} thickness={5} />
                        ) : (
                          <EmailConfirmationModal
                            theme={theme}
                            emailType={id}
                            title={name}
                            modalStyle={modalStyle}
                            handleSendMail={handleSendMail}
                          />
                        )}

                        <Chip
                          label={sent.status ? "Sent" : "Not Sent"}
                          size="small"
                          sx={{
                            fontSize: 10,
                            height: 20,
                            fontWeight: 500,
                            backgroundColor: sent.status
                              ? "#EBFBEB"
                              : "#ebebebff",
                            color: sent.status ? "#4E8B6E" : "#6b6a6a",
                          }}
                        />

                        {!!sent.mailCount && (
                          <CustomTooltip
                            styleProps={{
                              boxShadow: "none",
                              backgroundColor: "#3d3d3dff",
                              color: "#fff",
                            }}
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: {
                                      offset: [0, -10],
                                    },
                                  },
                                ],
                              },
                            }}
                            title={
                              <List
                                dense
                                disablePadding
                                sx={{ overflow: "hidden" }}
                              >
                                {(sent.date || []).map((d, i) => (
                                  <ListItem
                                    key={i}
                                    sx={{ boxShadow: "0 1px 1px #5c5c5c" }}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: "auto",
                                        marginRight: "5px",
                                      }}
                                    >
                                      <FiberManualRecordIcon
                                        sx={{
                                          fontSize: "6px",
                                          color: "#ffffff",
                                        }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={formatDate(d, "long")}
                                      slotProps={{
                                        primary: {
                                          fontSize: "10px",
                                        },
                                      }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            }
                            placement="bottom-end"
                          >
                            <InfoOutlined sx={{ fontSize: "15px" }} />
                          </CustomTooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [theme, templates, loadingType]);

  const historyTab = useMemo(() => {
    const history = [...(theme?.history || [])].reverse();

    return (
      <TableContainer
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: 2,
          height: "40vh",
        }}
        component={Box}
        className="email-history-table"
      >
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Theme Schema</TableCell>
              <TableCell>Theme Name</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {history?.length ? (
              history.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item?.themeSchemaName}</TableCell>

                  <TableCell
                    onMouseEnter={() => {
                      setHoverIndex(index);
                      setHoverField("themeName");
                    }}
                    onMouseLeave={() => {
                      setHoverIndex(null);
                      setHoverField(null);
                    }}
                  >
                    <DomainCell
                      field={"themeName"}
                      domain={theme.domain}
                      value={item?.themeName}
                      themeId={item.themeId}
                      index={index}
                      hoverIndex={hoverIndex}
                      hoverField={hoverField}
                    />
                  </TableCell>

                  <TableCell>{item?.themeVersion}</TableCell>

                  <TableCell>
                    <RoleChip role={item?.themeRole} />
                  </TableCell>
                  <TableCell>{item?.email}</TableCell>

                  <TableCell>{formatDate(item?.updated, "small")}</TableCell>

                  <TableCell>{item?.description}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ padding: "50px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      color: "text.secondary",
                      height: "100px",
                      mt: "20px",
                    }}
                  >
                    <InboxOutlined sx={{ fontSize: 38, opacity: 0.5 }} />
                    <Typography>History not found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table></TableContainer>
    );
  }, [theme, hoverField, hoverIndex]);

  return (
    <>
      <IconButton onClick={() => setOpen(true)}>
        <MoreVert />
      </IconButton>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.2,
            }}
          >
            <Typography fontWeight={300} variant="h6" sx={{ fontSize: "15px" }}>
              Email : {theme?.email}
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 2.5,
              border: "1px solid #E5E7EB",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              maxHeight: "100%",
            }}
          >
            <Stack direction="row" justifyContent={"space-between"} mb={2.5}>
              <Stack direction="row" gap={2}>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  color={activeTab === "history" ? "primary" : "inherit"}
                  onClick={() => setActiveTab("history")}
                >
                  History
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  color={activeTab === "email" ? "primary" : "inherit"}
                  onClick={() => setActiveTab("email")}
                >
                  Email
                </Button>
              </Stack>

              <FormControlLabel
                control={
                  subscriptionLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 42, height: 42 }}>
                      <CircularProgress size={16} thickness={5} />
                    </Box>
                  ) : (
                    <Checkbox
                      size="small"
                      checked={theme?.unSubscribed}
                      disabled={subscriptionLoading}
                      onChange={(e) =>
                        updateSubscription(theme?.domain, theme?.themeSchemaName, e.target.checked)
                      }
                    />
                  )
                }
                label="Mark for Unsubscribe"
              />
            </Stack>

            {/* <TableContainer
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 2,
              }}
              component={Box}
              className="email-history-table"
            > */}
            {activeTab === "history" ? historyTab : emailTab}
            {/* </TableContainer> */}
          </Box>
        </Box>
      </Modal>
      {/* Toasts */}
      <Snackbar
        open={toast.success}
        autoHideDuration={800}
        onClose={() => setToast({ ...toast, success: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{toast.msg}</Alert>
      </Snackbar>

      <Snackbar
        open={toast.error}
        autoHideDuration={1200}
        onClose={() => setToast({ ...toast, error: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{toast.msg}</Alert>
      </Snackbar>
    </>
  );
};

export default EmailAction;
